import React, { Suspense, lazy } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Redirect, Switch } from 'react-router-dom';
import { createStore, applyMiddleware, compose, combineReducers } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { ToastContainer } from 'react-toastify';

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import {
  // ActionPerformed,
  // PushNotificationSchema,
  PushNotifications,
  // Token,
} from '@capacitor/push-notifications';

import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.css';
import 'react-day-picker/lib/style.css';

import authReducer from '../../store/reducers/auth';
import tasksReducer from '../../store/reducers/tasks';
import labelsReducer from '../../store/reducers/labels';
import { watchAuth, watchTasks, watchLabels } from '../../store/sagas';
import * as ROUTES from '../../constants/routes';
import styles from './app.module.css';

import Loading from '../Loading';

const Auth = lazy(() => {
  return import(/* webpackChunkName: 'Auth' */ '../Auth');
});
const CreateTask = lazy(() => {
  return import(/* webpackChunkName: 'CreateTask' */ '../CreateTask');
});
const Navigation = lazy(() => {
  return import(/* webpackChunkName: 'Navigation' */ '../Navigation');
});
const PrivateRoute = lazy(() => {
  return import(/* webpackChunkName: 'PrivateRoute' */ '../PrivateRoute');
});
const PublicRoute = lazy(() => {
  return import(/* webpackChunkName: 'PublicRoute' */ '../PublicRoute');
});
const SignUp = lazy(() => {
  return import(/* webpackChunkName: 'SignUp' */ '../SignUp');
});
const SignIn = lazy(() => {
  return import(/* webpackChunkName: 'SignIn' */ '../SignIn');
});
const SignInWithToken = lazy(() => {
  return import(/* webpackChunkName: 'SignIn' */ '../SignInWithToken');
});
const CheckEmail = lazy(() => {
  return import(/* webpackChunkName: 'CheckEmail' */ '../CheckEmail');
});
const PasswordForget = lazy(() => {
  return import(/* webpackChunkName: 'PasswordForget' */ '../PasswordForget');
});
const PasswordReset = lazy(() => {
  return import(/* webpackChunkName: 'PasswordReset' */ '../PasswordReset');
});
const PasswordResetInfoPage = lazy(() => {
  return import(/* webpackChunkName: 'PasswordResetInfoPage' */ '../PasswordForget/PasswordResetInfoPage');
});
const Task = lazy(() => {
  return import(/* webpackChunkName: 'Task' */ '../Task');
});
const Tasks = lazy(() => {
  return import(/* webpackChunkName: 'Tasks' */ '../Tasks');
});
const DelayedTasks = lazy(() => {
  return import(/* webpackChunkName: 'DelayedTasks' */ '../DelayedTasks');
});
const List = lazy(() => {
  return import(/* webpackChunkName: 'List' */ '../List');
});
const AccountPage = lazy(() => {
  return import(/* webpackChunkName: 'Account' */ '../Account');
});

const devTools = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
const composeEnhancers = process.env.NODE_ENV === 'development' && devTools ? devTools : compose;

const rootReducer = combineReducers({
  auth: authReducer,
  tasks: tasksReducer,
  labels: labelsReducer,
});

const sagaMiddleware = createSagaMiddleware();

const store = createStore(rootReducer, composeEnhancers(applyMiddleware(sagaMiddleware)));

sagaMiddleware.run(watchAuth);
sagaMiddleware.run(watchTasks);
sagaMiddleware.run(watchLabels);

CapacitorApp.addListener('appUrlOpen', (data) => {
  try {
    const url = data && data.url ? data.url : '';
    if (url) {
      window.location.href = url;
    }
  } catch (error) {
    // eslint-disable-next-line
    console.log('Capacitor error: ', error);
  }
});

const isPushNotificationsAvailable = Capacitor.isPluginAvailable('PushNotifications');
if (isPushNotificationsAvailable) {
  // Request permission to use push notifications
  // iOS will prompt user and return if they granted permission or not
  // Android will just grant without prompting
  PushNotifications.requestPermissions().then((result) => {
    if (result.receive === 'granted') {
      // Register with Apple / Google to receive push via APNS/FCM
      PushNotifications.register();
    } else {
      // eslint-disable-next-line
      console.log('PushNotifications register error');
    }
  });
}

const App = () => {
  return (
    <Suspense fallback={<Loading />}>
      <Provider store={store}>
        <BrowserRouter>
          <div className={styles.appWrap} data-testid='App'>
            <Auth />
            <Navigation className={styles.navigationWrap} />

            <ToastContainer />

            <Switch>
              <PublicRoute path={ROUTES.SIGN_UP} component={SignUp} />
              <PublicRoute path={ROUTES.SIGN_IN} component={SignIn} />
              <PublicRoute path={ROUTES.SIGN_IN_WITH_TOKEN} component={SignInWithToken} />
              <PublicRoute path={ROUTES.CHECK_EMAIL} component={CheckEmail} />
              <PublicRoute path={ROUTES.PASSWORD_RESET_CHECK_EMAIL} component={PasswordResetInfoPage} />
              <PublicRoute path={ROUTES.PASSWORD_FORGET} component={PasswordForget} />
              <PublicRoute path={ROUTES.PASSWORD_RESET} component={PasswordReset} />
              <PrivateRoute path={ROUTES.TASK} component={Task} />
              <PrivateRoute exact path={ROUTES.TASKS} component={Tasks} />
              <PrivateRoute path={ROUTES.DELAYED_TASKS} component={DelayedTasks} />
              <PrivateRoute path={ROUTES.LIST} component={List} />
              <PrivateRoute path={ROUTES.ACCOUNT} component={AccountPage} />
              <PrivateRoute path={ROUTES.CREATE_TASK} component={CreateTask} />

              <Redirect from='*' to={ROUTES.SIGN_IN} />
            </Switch>
          </div>
        </BrowserRouter>
      </Provider>
    </Suspense>
  );
};

export default App;
